import React from "react";
import "./Card.css";
import { IconContext } from "react-icons";
import { AiOutlineClose } from "react-icons/ai";
import { VscCopy } from "react-icons/vsc";
import ReactMarkdown from 'react-markdown';
import ErrorBoundary from './ErrorBoundary';
import OpenAI from "./images/openai.png";
import HuggingFace from "./images/huggingfaceicon.png";
import anthropic from "./images/anthropic.png";
import PaLM from "./images/Google_PaLM_Logo.png";
import Gemini from "./images/Google-Gemini.webp";
import GrokAI from "./images/GrokAI.png";
import rehypeRaw from 'rehype-raw';
import { CardProps, CardState } from "../types";

interface ModelData {
    title: string;
    src?: string;
    alt?: string;
    extraText: string;
}

class Card extends React.Component<CardProps, CardState> {
    constructor(props: CardProps) {
        super(props);
        this.state = {
            usage: this.props.usage || "",
            output: "",
            model: this.props.model,
            cardCreated: this.props.cardCreated,
            darkMode: this.props.darkMode || false,
            cardOutput: "",
            output2: ""
        }
        this.ai = this.ai.bind(this);
    }

    static getDerivedStateFromProps(nextProps: CardProps, prevState: CardState): Partial<CardState> | null {
        if (nextProps.darkMode !== prevState.darkMode) {
            return {
                darkMode: nextProps.darkMode || false
            };
        }
        return null;
    }

    renderMarkdown(content: string | React.ReactNode | any[]): React.ReactNode {
        if (!content) {
            return null;
        }

        // If it's a React element, skip rendering
        if (React.isValidElement(content)) {
            return null;
        }

        let text = '';
        try {
            if (typeof content === 'string') {
                text = content;
            } else if (Array.isArray(content)) {
                // Handle array of strings or React elements
                text = content.map(item => {
                    if (typeof item === 'string') {
                        return item;
                    } else if (React.isValidElement(item)) {
                        return ''; // Skip React elements
                    } else if (typeof item === 'object') {
                        return JSON.stringify(item);
                    }
                    return String(item);
                }).join('');
            } else if (content && typeof content === 'object') {
                text = JSON.stringify(content);
            }
        } catch (e) {
            console.error('Error converting content to string:', e);
            return null;
        }

        if (!text || text === 'null' || text === 'undefined') {
            return null;
        }

        return (
            <ErrorBoundary>
                <div className="line markdown-content">
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                        {text}
                    </ReactMarkdown>
                </div>
            </ErrorBoundary>
        );
    }

    ai(): React.ReactNode {
        const modelData: Record<string, ModelData> = {
            "hf": {
                title: "HuggingFace",
                src: HuggingFace,
                alt: "huggingface",
                extraText: "@meta-llama/Llama-2-7b-chat-hf"
            },
            "openai": {
                title: "OpenAI",
                src: OpenAI,
                alt: "openai",
                extraText: ""
            },
            "anthropic": {
                title: "Anthropic",
                src: anthropic,
                alt: "anthropic",
                extraText: ""
            },
            "Grok AI": {
                title: "Grok AI",
                src: GrokAI,
                alt: "Grok AI",
                extraText: ""
            },
            "Google Gemini": {
                title: "Google Gemini",
                src: Gemini,
                alt: "Gemini",
                extraText: ""
            },
            "assistant": {
                title: "assistant",
                extraText: ""
            }
        };

        const currentModel = modelData[this.props.model];
        const { output } = this.props;

        if (!currentModel) {
            return null;
        }

        const renderContent = (): React.ReactNode => {
            if (currentModel.title === "assistant" && !output) {
                return null;
            }

            const markdownContent = this.renderMarkdown(output);
            if (!markdownContent) {
                return null;
            }

            return (
                <div className="cardInfo">
                    <div className="cardTitle">
                        {currentModel.title !== "assistant" && currentModel.src && (
                            <img
                                title={currentModel.title}
                                className="openai"
                                src={currentModel.src}
                                alt={currentModel.alt}
                            />
                        )}
                        <div className="codex poweredBy neonText">
                            {currentModel.title} {currentModel.extraText}
                        </div>
                    </div>
                    {markdownContent}
                    <p className={this.state.darkMode === false ? "cardCreated" : "cardCreated-dark"}>
                        {this.props.cardCreated}
                    </p>
                </div>
            );
        };

        return renderContent();
    }

    render(): React.ReactNode {
        const renderIconButton = (icon: React.ReactNode, action: () => void): React.ReactNode => (
            <div className="iconButton" onClick={action}>
                <IconContext.Provider value={{ size: "1.35em", style: { paddingRight: "0.2rem", marginTop: "0.1rem" } }}>
                    {icon}
                </IconContext.Provider>
            </div>
        );

        return (
            <ErrorBoundary>
                <div className={this.state.darkMode === false ? "card" : "card-dark"}>
                    <div className="cardBtns">
                        <div className="closeBtn" onClick={() => this.props.closeCard(this.props.index)}>
                            <IconContext.Provider value={{ size: "1.35em", style: { paddingRight: "0.2rem", marginTop: "0.1rem" } }}>
                                <AiOutlineClose />
                            </IconContext.Provider>
                        </div>
                        <div className="copyBtn" onClick={() => this.props.copyCard(this.props.index)}>
                            <IconContext.Provider value={{ size: "1.35em", style: { paddingRight: "0.2rem", marginTop: "0.1rem" } }}>
                                <VscCopy />
                            </IconContext.Provider>
                        </div>
                    </div>
                    {this.ai()}
                </div>
            </ErrorBoundary>
        );
    }
}

export default Card;
